import { ChangeDetectionStrategy, Component, Input, OnInit, computed, inject } from '@angular/core';

import { Observable, of, map } from 'rxjs';
import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon, SvgLibraryNewIcon } from '@finnairoyj/fcom-ui-styles';

import { AppState } from '@fcom/core/interfaces/app-state.interface';
import { NativeBridgeService } from '@fcom/core/services/native-bridge/native-bridge.service';
import { casProfileName, casProfileOrNot, loginStatus } from '@fcom/core/selectors/login.selector';
import { CasCustomerType, CasProfile, LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { finShare } from '@fcom/rx/operators/finShare';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconButtonTheme } from '@fcom/ui-components/components/buttons/icon-button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { IconSet } from '@fcom/ui-components/components/icons/icons.interfaces';

import { ElementTypes } from '../../interfaces/gtm.interface';
import { FinnairThemeService } from '../../services/theme/finnair-theme.service';
import { MediaQueryService } from '../../services/media-query/media-query.service';
import { FinnairTheme } from '../../interfaces/theme.interface';

const SHOW_BUTTON_STATES = [
  LoginStatus.LOGGED_IN,
  LoginStatus.NOT_LOGGED_IN,
  LoginStatus.ERROR,
  LoginStatus.PENDING,
  LoginStatus.INITIAL,
];

@Component({
  selector: 'fin-login-button',
  templateUrl: 'login-button.component.html',
  styleUrls: ['./login-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoginButtonComponent implements OnInit {
  private store$ = inject<Store<AppState>>(Store);
  private nativeBridgeService = inject(NativeBridgeService);
  private mediaQueryService = inject(MediaQueryService);
  private themeService = inject(FinnairThemeService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly ElementTypes = ElementTypes;
  readonly IconPosition = IconPosition;
  readonly LoginStatus = LoginStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly SvgLibraryNewIcon = SvgLibraryNewIcon;
  readonly IconLibrary = IconLibrary;

  showButton$: Observable<boolean>;
  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');
  icon$: Observable<IconSet>;

  profileName$: Observable<string>;
  loginStatus$: Observable<LoginStatus>;

  @Input()
  showName = false;

  @Input()
  nameStyle = 'font-body-1 font-body-medium';

  @Input()
  trackingContext: string;

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  ngOnInit(): void {
    this.profileName$ = this.store$.pipe(casProfileName());
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.icon$ = this.store$.pipe(
      casProfileOrNot(),
      map((casProfile) => this.getIcon(casProfile))
    );
    this.showButton$ = this.nativeBridgeService.isInsideNativeWebView
      ? of(false)
      : this.loginStatus$.pipe(
          map((s) => SHOW_BUTTON_STATES.indexOf(s) !== -1),
          finShare()
        );
  }

  private getIcon(casProfile: CasProfile | undefined): IconSet {
    const isCorporate = casProfile?.customerType === CasCustomerType.CORPORATE_USER;

    if (!this.isNewBrand()) {
      const name = !casProfile
        ? SvgLibraryIcon.SINGLE_PAX
        : isCorporate
          ? SvgLibraryIcon.B2B
          : SvgLibraryIcon.LOGGED_IN;

      return { category: IconLibrary.SVG_LIBRARY, name };
    }

    const name = !casProfile
      ? SvgLibraryNewIcon.SINGLE_PAX
      : isCorporate
        ? SvgLibraryNewIcon.B2B
        : SvgLibraryNewIcon.PROFILE;

    return { category: IconLibrary.SVG_LIBRARY_NEW, name };
  }
}
